import React, { useState } from 'react';

import { useMutation, useQuery } from '@apollo/client';
import clsx from 'clsx';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { InputText } from 'primereact/inputtext';
import { TabPanel, TabView } from 'primereact/tabview';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { addToast } from '@/app/slices/toast';
import { Button } from '@/components/ui/button';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useAccessToken } from '@/services/auth';
import { updateRevisionsBatch } from '@/services/chute-scheduler/api';
import {
  useAspDataPoolQuery,
  useCreateAspPool,
  useRemoveAspPool,
} from '@/services/opu-search/hooks';
import type { PoolResponse } from '@/services/opu-search/types';
import {
  AspirationPool,
  GET_ASPIRATION_POOLS_BY_DATE_AND_SITE,
  UPDATE_REVISIONS,
  UpdateRevisionsInput,
} from '@/types.gql';

type Props = {
  setShowPoolAspirations: React.Dispatch<React.SetStateAction<boolean>>;
  refetch: () => void;
  poolNumArray: number[];
};

export const PoolAspirationsDialog = ({
  setShowPoolAspirations,
  refetch,
  poolNumArray,
}: Props) => {
  const dispatch = useDispatch();
  const token = useAccessToken();
  const [params] = useSearchParams();
  const aspirationDate = params.get('date') || '';
  const siteId = params.get('site') || '';
  const isBatchPoolRevisionEnabled = useFeatureFlag(
    'Batch_Pool_Revision_Comment',
  );
  const tabViewClassName = clsx('tab-view-shading');

  const [selectedAspirations, setSelectedAspirations] = useState<
    PoolResponse[]
  >([]);
  const [selectedRemovedAspirations, setSelectedRemovedAspirations] = useState<
    AspirationPool[]
  >([]);
  const [filter, setFilter] = useState('');

  const { data: aspirationPoolData, refetch: refetchAspirationPool } =
    useAspDataPoolQuery({
      aspirationDate,
      siteId,
    });

  const { data: existingAspirationPools, refetch: refetchAspirationPools } =
    useQuery(GET_ASPIRATION_POOLS_BY_DATE_AND_SITE, {
      variables: { siteId: siteId, poolNumArray: poolNumArray },
      fetchPolicy: 'network-only',
      skip: !aspirationDate || !siteId,
    });

  const { mutateAsync: createNewAspirationPool } = useCreateAspPool();

  const { mutateAsync: removeFromAspirationPool } = useRemoveAspPool();

  const [updateRevisions] = useMutation(UPDATE_REVISIONS);

  const renderHeader = () => {
    return (
      <InputText
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        placeholder="Search Donor"
      />
    );
  };

  const handleRemoveFromPool = () => {
    const aspirationPoolIds = selectedRemovedAspirations.map((pool) => pool.id);
    removeFromAspirationPool({
      aspirationPoolIds: aspirationPoolIds,
    }).then(async () => {
      setShowPoolAspirations(false);
      refetchAspirationPool();
      refetchAspirationPools();
      refetch();
    });
  };

  const handlePoolAspirations = async () => {
    if (selectedAspirations.length === 0) {
      return;
    }

    const firstAspiration = selectedAspirations[0];
    const { sireName, semenName, semenTypeName } = firstAspiration;

    const hasMismatch = selectedAspirations.some((aspiration) => {
      return (
        aspiration.sireName !== sireName ||
        aspiration.semenName !== semenName ||
        aspiration.semenTypeName !== semenTypeName
      );
    });

    if (hasMismatch) {
      dispatch(
        addToast({
          severity: 'error',
          summary: 'Cannot Create Pool',
          detail:
            'All selected aspirations must have the same sire, semen name, and semen type.',
          sticky: false,
          closable: true,
        }),
      );
      return;
    }

    const aspirationIds = selectedAspirations.map((pool) => pool.aspirationId);
    const poolResult = await createNewAspirationPool({
      aspirationIds: aspirationIds,
      siteId: siteId,
    });

    const pathNameIncludesChuteScheduler = new URL(
      window.location.href,
    ).pathname.includes('chute-scheduler');

    if (poolResult && pathNameIncludesChuteScheduler) {
      if (isBatchPoolRevisionEnabled) {
        await updateRevisionsBatch(token, {
          donorScheduleIds: selectedAspirations.map(
            (aspiration) => aspiration.donorScheduleId,
          ),
          revisionComment: `Pool ${poolResult}, sire choice ${sireName}`,
        });
      } else {
        updateRevisions({
          variables: {
            updates: selectedAspirations.map((aspiration) => ({
              donorScheduleId: aspiration.donorScheduleId,
              revisionComment: `Pool ${poolResult}, sire choice ${sireName}`,
            })) as UpdateRevisionsInput[],
          },
        });
      }
    }

    setShowPoolAspirations(false);
    refetchAspirationPool();
    refetchAspirationPools();
    refetch();
  };

  return (
    <div className="space-y-4">
      <TabView className={tabViewClassName}>
        <TabPanel header="Pool Aspirations">
          <DataTable
            value={aspirationPoolData}
            sortField="donorName"
            sortOrder={1}
            scrollable
            scrollHeight="25vw"
            selection={selectedAspirations}
            onSelectionChange={(e) => setSelectedAspirations(e.value)}
            dataKey="aspirationId"
            selectionMode="multiple"
            header={renderHeader}
            globalFilter={filter}
          >
            <Column selectionMode="multiple" headerStyle={{ width: '3rem' }} />
            <Column field="donorName" header="Donor Name" />
            <Column field="gradeAbc" header="Grade ABC" />
            <Column field="gradeD" header="Grade D" />
            <Column field="gradeE" header="Grade E" />
            <Column field="sireName" header="Sire Name" />
            <Column field="semenName" header="Semen Name" />
            <Column field="semenTypeName" header="Semen Type" />
          </DataTable>
          <div className="mt-4 flex justify-end gap-2">
            <Button
              variant="outline"
              className="gap-1.5"
              onClick={() => setShowPoolAspirations(false)}
            >
              <i className="pi pi-times text-xs" />
              Close
            </Button>
            <Button
              disabled={!(selectedAspirations.length > 0) || !siteId}
              onClick={handlePoolAspirations}
            >
              Pool
            </Button>
          </div>
        </TabPanel>

        <TabPanel header="Remove from Pool">
          <DataTable
            value={
              existingAspirationPools?.aspirationPools
                ?.items as AspirationPool[]
            }
            sortOrder={1}
            scrollable
            scrollHeight="25vw"
            selection={selectedRemovedAspirations}
            onSelectionChange={(e) => setSelectedRemovedAspirations(e.value)}
            dataKey="id"
            selectionMode="multiple"
          >
            <Column selectionMode="multiple" headerStyle={{ width: '3rem' }} />
            {/* <Column field="id" header="id" /> */}
            <Column field="name" header="Name" />
            <Column field="poolNumber" header="Pool Number" />
          </DataTable>
          <div className="mt-4 flex justify-end gap-2">
            <Button
              variant="outline"
              className="gap-1.5"
              onClick={() => setShowPoolAspirations(false)}
            >
              <i className="pi pi-times text-xs" />
              Close
            </Button>
            <Button
              variant="destructive"
              disabled={!(selectedRemovedAspirations.length > 0) || !siteId}
              onClick={handleRemoveFromPool}
            >
              Remove from Pool
            </Button>
          </div>
        </TabPanel>
      </TabView>
    </div>
  );
};
